/**
 * EmailDraftRenderer — Sprint 2 hand-designed approval renderer.
 *
 * Per spec §8.5, V1 ships ONE hand-designed renderer plus the auto-renderer.
 * email_draft is the highest-leverage choice: a Gmail / Outlook-shaped
 * compose surface with thread context above and recipient + subject + body
 * fields below.
 *
 * Schema contract (workflow author declares editableFields with these
 * canonical paths — they fall through to the auto-renderer if missing):
 *   - content.draft.to           → recipients
 *   - content.draft.cc           → recipients
 *   - content.draft.bcc          → recipients
 *   - content.draft.subject      → text
 *   - content.draft.body         → multiline / rich_text
 * Plus an optional thread context panel reading `content.previous_emails`.
 *
 * Notes:
 *   - No new dependencies — body is a tall textarea, not a WYSIWYG. The
 *     spec lists "rich quote handling" as polish, but Sprint 2's goal is
 *     "ergonomically optimized", not "match Outlook pixel-for-pixel".
 *   - AIEditBox is wired on every aiEditable field for in-place rewriting
 *     ("shorten", "more formal") in line with the workflow.
 */

import * as React from 'react';
import { Mail, ChevronDown, ChevronRight } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { readPath, writePath } from '@argusai/shared';
import type { FieldDef } from '@argusai/shared';
import { FieldRow } from '../AutoRenderedApprovalCard';
import { ActionBar } from '../ActionBar';
import { cn } from '@/lib/utils';
import type { RendererComponent } from '../AutoRenderedApprovalCard';

// Canonical paths the schema author SHOULD declare. We tolerate missing
// fields — if e.g. `cc` isn't declared the row doesn't render.
const CANONICAL_PATHS = {
  to: 'content.draft.to',
  cc: 'content.draft.cc',
  bcc: 'content.draft.bcc',
  subject: 'content.draft.subject',
  body: 'content.draft.body',
  threadPath: 'content.previous_emails',
} as const;

function findField(fields: FieldDef[], path: string): FieldDef | undefined {
  return fields.find((f) => f.path === path);
}

// Inline writePath shim that resolves to a string for the local input
// state. We don't push every keystroke up — see body below.
function asString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  return String(v);
}

function asArray(v: unknown): unknown[] {
  if (Array.isArray(v)) return v;
  return [];
}

function ThreadContext({
  thread,
  collapsedByDefault = true,
}: {
  thread: unknown[];
  collapsedByDefault?: boolean;
}) {
  const [open, setOpen] = React.useState(!collapsedByDefault);
  if (!Array.isArray(thread) || thread.length === 0) return null;

  return (
    <section
      className="rounded-md border border-border/60 bg-muted/30"
      data-thread-context="true"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-muted-foreground hover:text-foreground"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        Thread context ({thread.length}{' '}
        {thread.length === 1 ? 'message' : 'messages'})
      </button>
      {open && (
        <ul className="divide-y divide-border/60 border-t border-border/60">
          {thread.map((entry, i) => {
            const e = entry as Record<string, unknown>;
            const from = asString(e?.from ?? e?.sender ?? 'unknown');
            const subject = asString(e?.subject ?? '(no subject)');
            const snippet = asString(e?.snippet ?? e?.body ?? '');
            const at = asString(e?.at ?? e?.date ?? '');
            return (
              <li key={i} className="px-3 py-2 text-xs">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium">{from}</span>
                  {at && (
                    <time className="text-[10px] text-muted-foreground">
                      {at}
                    </time>
                  )}
                </div>
                <div className="font-medium text-muted-foreground">
                  {subject}
                </div>
                {snippet && (
                  <p className="mt-1 line-clamp-2 text-muted-foreground">
                    {snippet}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export const EmailDraftRenderer: RendererComponent = ({
  approval,
  schema,
  evidence,
  onFieldChange,
  onDecide,
  disabled,
}) => {
  const fields = schema.editableFields;
  const toField = findField(fields, CANONICAL_PATHS.to);
  const ccField = findField(fields, CANONICAL_PATHS.cc);
  const bccField = findField(fields, CANONICAL_PATHS.bcc);
  const subjectField = findField(fields, CANONICAL_PATHS.subject);
  const bodyField = findField(fields, CANONICAL_PATHS.body);

  // Any fields the email schema doesn't claim → render at the bottom so
  // workflow authors can extend the draft (signature, attachments) without
  // breaking the canonical surface.
  const claimed = new Set<string>([
    CANONICAL_PATHS.to,
    CANONICAL_PATHS.cc,
    CANONICAL_PATHS.bcc,
    CANONICAL_PATHS.subject,
    CANONICAL_PATHS.body,
  ]);
  const extras = fields.filter((f) => !claimed.has(f.path));

  const thread = asArray(readPath(evidence, CANONICAL_PATHS.threadPath));

  // Context panels still render on the right rail if declared.
  return (
    <div className="grid gap-6 p-4 lg:grid-cols-[2fr,1fr]">
      <div className="space-y-3" data-renderer="email_draft">
        <div className="flex items-center gap-2 text-sm font-semibold">
          <Mail className="h-4 w-4 text-primary" />
          <span>Compose</span>
        </div>

        {thread.length > 0 && <ThreadContext thread={thread} />}

        {/* Recipient rows */}
        <div className="space-y-2">
          {toField && (
            <FieldRow
              approvalId={approval.id}
              field={toField}
              value={readPath(evidence, toField.path)}
              onChange={(v) => onFieldChange(toField.path, v)}
              disabled={disabled}
            />
          )}
          {ccField && (
            <FieldRow
              approvalId={approval.id}
              field={ccField}
              value={readPath(evidence, ccField.path)}
              onChange={(v) => onFieldChange(ccField.path, v)}
              disabled={disabled}
            />
          )}
          {bccField && (
            <FieldRow
              approvalId={approval.id}
              field={bccField}
              value={readPath(evidence, bccField.path)}
              onChange={(v) => onFieldChange(bccField.path, v)}
              disabled={disabled}
            />
          )}
        </div>

        {/* Subject — slightly larger to match email compose conventions */}
        {subjectField && (
          <div className="rounded-md border border-border/60 bg-background p-2">
            <FieldRow
              approvalId={approval.id}
              field={{
                ...subjectField,
                label: subjectField.label || 'Subject',
              }}
              value={readPath(evidence, subjectField.path)}
              onChange={(v) => onFieldChange(subjectField.path, v)}
              disabled={disabled}
            />
          </div>
        )}

        {/* Body — large compose surface */}
        {bodyField && (
          <div className="rounded-md border border-border/60 bg-background p-2">
            <FieldRow
              approvalId={approval.id}
              field={{
                ...bodyField,
                // Force a tall textarea even if the schema says 'text'.
                type:
                  bodyField.type === 'text' || bodyField.type === 'multiline'
                    ? 'rich_text'
                    : bodyField.type,
                label: bodyField.label || 'Body',
              }}
              value={readPath(evidence, bodyField.path)}
              onChange={(v) => onFieldChange(bodyField.path, v)}
              disabled={disabled}
            />
          </div>
        )}

        {/* Extras — anything the workflow author wants on the email surface
            beyond the canonical 5 fields. */}
        {extras.length > 0 && (
          <div className="space-y-3 border-t border-border/60 pt-3">
            {extras.map((f) => (
              <FieldRow
                key={f.path}
                approvalId={approval.id}
                field={f}
                value={readPath(evidence, f.path)}
                onChange={(v) => onFieldChange(f.path, v)}
                disabled={disabled}
              />
            ))}
          </div>
        )}

        <ActionBar
          actions={schema.actions}
          approvalId={approval.id}
          onDecide={onDecide}
          disabled={disabled}
          className="rounded-md"
        />
      </div>

      {/* Right column — context panels (CRM card, related docs, etc.) */}
      <aside className="space-y-3">
        {schema.contextPanels.length === 0 && (
          <p className="text-xs text-muted-foreground italic">
            No context panels declared
          </p>
        )}
        {schema.contextPanels.map((panel) => (
          <ContextPanelLite
            key={panel.panelId}
            title={panel.title}
            collapsedByDefault={panel.collapsedByDefault}
          >
            <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-words font-mono text-[10px]">
              {JSON.stringify(
                panel.dataPath
                  ? readPath(evidence, panel.dataPath)
                  : panel.fields?.length
                    ? Object.fromEntries(
                        panel.fields.map((f) => [
                          f.label,
                          readPath(evidence, f.path) ?? null,
                        ]),
                      )
                    : evidence,
                null,
                2,
              )}
            </pre>
          </ContextPanelLite>
        ))}
      </aside>
    </div>
  );
};

// Internal lite version of ContextPanel — the email-draft renderer wants
// tighter visual rhythm with the compose surface above. Same a11y
// contract: header is a button, content is in a labeled region.
function ContextPanelLite({
  title,
  collapsedByDefault = false,
  children,
}: {
  title: string;
  collapsedByDefault?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(!collapsedByDefault);
  return (
    <section className="rounded-md border border-border/60">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        {title}
      </button>
      {open && (
        <div className="border-t border-border/60 p-3">
          {/* The auto-renderer styles its panels but the inline version
              keeps the compose surface visually contiguous with the body. */}
          <Label className="sr-only">{title}</Label>
          <div className={cn('text-xs')}>{children}</div>
        </div>
      )}
    </section>
  );
}
